import type { GetSessionSnapshotResponse } from "@nlc/shared-types";

import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMultipartUploadMutations } from "@/features/my-files/components/upload-files-modal/hooks/useMultipartUploadMutations";

import type { FileVersionMetadata } from "../types/fileVersionMetadata";
import type { InFlightUpload } from "../types/sessionFile";
import type { useConfidenceCheckerMyFiles } from "./useConfidenceCheckerMyFiles";

import {
  attachSessionAttachments,
  deleteSessionAttachment,
  replaceSessionAttachment,
} from "../api/confidence-checker.api";
import { reconcileInFlightUploads } from "../utils/reconcileInFlightUploads";
import {
  buildNamedUploadFile,
  createInitialFileMetadata,
} from "../utils/sessionFileNaming";
import { uploadMyFileMultipart } from "../utils/uploadMyFileMultipart";

interface UseSessionUploadsOptions {
  carryOverOnAttach: (clientId: string, attachmentId: number) => boolean;
  claimSessionId: () => string;
  designerId: number | undefined;
  fileMetadataRef: MutableRefObject<Map<string, FileVersionMetadata>>;
  invalidateSnapshot: () => void;
  invalidateSnapshotFor: (targetSessionId: string) => void;
  localFilesRef: MutableRefObject<Map<number, File>>;
  requestId: number;
  requestMyFiles: ReturnType<typeof useConfidenceCheckerMyFiles>["data"];
  sessionId: null | string;
  setSessionId: (value: null | string) => void;
  snapshot: GetSessionSnapshotResponse | undefined;
  syncSnapshot: (
    nextSnapshot: GetSessionSnapshotResponse,
    targetSessionId: string,
  ) => void;
  updateFileMetadata: (
    clientId: string,
    patch: Partial<FileVersionMetadata>,
  ) => void;
}

interface UseSessionUploadsReturn {
  addFiles: (files: File[]) => Promise<void>;
  cancelUpload: (clientId: string) => void;
  pendingUploads: InFlightUpload[];
  removeFile: (input: {
    attachmentId?: number;
    clientId: string;
  }) => Promise<void>;
  replaceFile: (input: { attachmentId: number; file: File }) => void;
  resetUploadState: () => void;
}

export function useSessionUploads({
  carryOverOnAttach,
  claimSessionId,
  designerId,
  fileMetadataRef,
  invalidateSnapshot,
  invalidateSnapshotFor,
  localFilesRef,
  requestId,
  requestMyFiles,
  sessionId,
  setSessionId,
  snapshot,
  syncSnapshot,
  updateFileMetadata,
}: UseSessionUploadsOptions): UseSessionUploadsReturn {
  const multipartMutations = useMultipartUploadMutations();
  const [pendingUploads, setPendingUploads] = useState<InFlightUpload[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    if (!snapshot) return;

    setPendingUploads((prev) => {
      const next = reconcileInFlightUploads(prev, snapshot);

      return next.length === prev.length ? prev : next;
    });
  }, [snapshot]);

  const uploadOne = useCallback(
    async (file: File, clientId: string, targetSessionId: string) => {
      if (designerId === undefined) {
        throw new Error("Missing current user id");
      }

      const abortController = new AbortController();

      abortControllersRef.current.set(clientId, abortController);

      try {
        patchUpload(setPendingUploads, clientId, {
          progress: 0,
          status: "uploading",
        });

        const metadata = fileMetadataRef.current.get(clientId);
        const uploadFile = metadata
          ? buildNamedUploadFile(file, metadata.displayBaseName)
          : file;

        const attachmentId = await uploadMyFileMultipart({
          abortController,
          file: uploadFile,
          mutations: multipartMutations,
          onProgress: (progress) => {
            patchUpload(setPendingUploads, clientId, { progress });
          },
          requestId,
        });

        abortControllersRef.current.delete(clientId);
        carryOverOnAttach(clientId, attachmentId);
        localFilesRef.current.set(attachmentId, uploadFile);
        patchUpload(setPendingUploads, clientId, {
          attachmentId,
          status: "attaching",
        });

        const nextSnapshot = await attachSessionAttachments(targetSessionId, {
          attachmentIds: [attachmentId],
          designerId,
          requestId,
        });

        syncSnapshot(nextSnapshot, targetSessionId);
        patchUpload(setPendingUploads, clientId, { status: "complete" });
      } catch (error) {
        abortControllersRef.current.delete(clientId);

        if (abortController.signal.aborted) {
          return;
        }

        patchUpload(setPendingUploads, clientId, {
          error: error instanceof Error ? error.message : "Upload failed",
          status: "error",
        });
        throw error;
      }
    },
    [
      carryOverOnAttach,
      designerId,
      fileMetadataRef,
      localFilesRef,
      multipartMutations,
      requestId,
      syncSnapshot,
    ],
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || designerId === undefined) return;

      const targetSessionId = claimSessionId();
      const entries: InFlightUpload[] = files.map((file) => ({
        clientId: crypto.randomUUID(),
        file,
        status: "uploading",
      }));

      for (const entry of entries) {
        updateFileMetadata(
          entry.clientId,
          createInitialFileMetadata(entry.file, requestMyFiles),
        );
      }

      setPendingUploads((prev) => [...prev, ...entries]);

      const uploadResults = await Promise.allSettled(
        entries.map((entry) =>
          uploadOne(entry.file, entry.clientId, targetSessionId),
        ),
      );

      const anySucceeded = uploadResults.some(
        (result) => result.status === "fulfilled",
      );

      if (anySucceeded) {
        setSessionId(targetSessionId);
        invalidateSnapshotFor(targetSessionId);
      }
    },
    [
      claimSessionId,
      designerId,
      invalidateSnapshotFor,
      requestMyFiles,
      setSessionId,
      updateFileMetadata,
      uploadOne,
    ],
  );

  const replaceFile = useCallback(
    ({ attachmentId, file }: { attachmentId: number; file: File }) => {
      if (designerId === undefined || sessionId === null) {
        throw new Error("Cannot replace file without an active session");
      }

      const clientId = crypto.randomUUID();
      const entry: InFlightUpload = {
        clientId,
        file,
        replacesAttachmentId: attachmentId,
        status: "uploading",
      };

      setPendingUploads((prev) => [...prev, entry]);

      void (async () => {
        const abortController = new AbortController();

        abortControllersRef.current.set(clientId, abortController);

        try {
          patchUpload(setPendingUploads, clientId, {
            progress: 0,
            status: "uploading",
          });

          const originalFile = localFilesRef.current.get(attachmentId);
          const uploadFile =
            originalFile && originalFile.name !== file.name
              ? new File([file], originalFile.name, {
                  lastModified: file.lastModified,
                  type: file.type,
                })
              : file;

          const newAttachmentId = await uploadMyFileMultipart({
            abortController,
            file: uploadFile,
            mutations: multipartMutations,
            onProgress: (progress) => {
              patchUpload(setPendingUploads, clientId, { progress });
            },
            requestId,
          });

          abortControllersRef.current.delete(clientId);
          localFilesRef.current.set(newAttachmentId, uploadFile);

          patchUpload(setPendingUploads, clientId, {
            attachmentId: newAttachmentId,
            status: "attaching",
          });

          const nextSnapshot = await replaceSessionAttachment(
            sessionId,
            attachmentId,
            {
              designerId,
              newAttachmentId,
              requestId,
            },
          );

          syncSnapshot(nextSnapshot, sessionId);
          localFilesRef.current.delete(attachmentId);
          invalidateSnapshot();

          setPendingUploads((prev) =>
            prev.filter((upload) => upload.clientId !== clientId),
          );
        } catch (error) {
          abortControllersRef.current.delete(clientId);

          if (abortController.signal.aborted) {
            setPendingUploads((prev) =>
              prev.filter((upload) => upload.clientId !== clientId),
            );

            return;
          }

          patchUpload(setPendingUploads, clientId, {
            error: error instanceof Error ? error.message : "Replace failed",
            status: "error",
          });
        }
      })();
    },
    [
      designerId,
      invalidateSnapshot,
      localFilesRef,
      multipartMutations,
      requestId,
      sessionId,
      syncSnapshot,
    ],
  );

  const cancelUpload = useCallback((clientId: string) => {
    const controller = abortControllersRef.current.get(clientId);

    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(clientId);
    }

    setPendingUploads((prev) =>
      prev.filter((upload) => upload.clientId !== clientId),
    );
  }, []);

  const removeFile = useCallback(
    async ({
      attachmentId,
      clientId,
    }: {
      attachmentId?: number;
      clientId: string;
    }) => {
      if (attachmentId !== undefined && sessionId) {
        await deleteSessionAttachment(sessionId, attachmentId);
        localFilesRef.current.delete(attachmentId);
        invalidateSnapshot();
      }
      setPendingUploads((prev) =>
        prev.filter((upload) => upload.clientId !== clientId),
      );
    },
    [invalidateSnapshot, localFilesRef, sessionId],
  );

  const resetUploadState = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    setPendingUploads([]);
    localFilesRef.current.clear();
  }, [localFilesRef]);

  return {
    addFiles,
    cancelUpload,
    pendingUploads,
    removeFile,
    replaceFile,
    resetUploadState,
  };
}

function patchUpload(
  setPendingUploads: Dispatch<SetStateAction<InFlightUpload[]>>,
  clientId: string,
  patch: Partial<InFlightUpload>,
) {
  setPendingUploads((prev) =>
    prev.map((upload) =>
      upload.clientId === clientId ? { ...upload, ...patch } : upload,
    ),
  );
}
